import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { UploadCloud, CheckCircle2, XCircle, Loader2, Split, AlertTriangle, Download } from "lucide-react";

import { apiGet, apiPatch, apiPost, unpackList } from "../../Api/Client.js";
import { Panel, EmptyState } from "../../Shared/ScreenComponents.jsx";
import { editRowOptimistically } from "./ledgerEdit.js";

/* P&L by vertical (Vidya, Interview God, Vikaas, Banao, Axon) plus Shared/
 * Overhead and Unassigned, one month at a time.
 *
 * This is a system of record, not a viewer: every uploaded statement's
 * transactions live in the database permanently, so the month picker below is
 * built from the months that actually hold data rather than a guessed range,
 * and reloading the page never loses anything.
 *
 * Deliberately self-fetching (not on the app-wide endpointMap prefetch) —
 * transaction volume only grows, and the CRM screen already paid for the
 * lesson that a global full-table prefetch doesn't stay fast forever.
 */

const VERTICALS = ["Vidya", "Interview God", "Vikaas", "Banao", "Axon", "Shared / Overhead", "Bunny Toys"];
/* The five live verticals are always shown, even at zero, so the shape of the
   table doesn't shift underfoot month to month. Anything else — Bunny Toys,
   which closed — only appears in periods where it actually has numbers, which
   keeps the table narrow enough to read in recent months. */
const ALWAYS_SHOWN = ["Vidya", "Interview God", "Vikaas", "Banao", "Axon", "Shared / Overhead"];
const CATEGORY_ROWS = [
  "Revenue", "Direct / Delivery Costs", "Salaries & Stipends", "Infra & SaaS",
  "G&A / Overhead", "Taxes & Compliance", "Bank Charges",
];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* India files April to March. That is the year a founder means by "this year",
   and the year an accountant or a lawyer can be handed without a re-cut. */
function currentFyStart(date = new Date()) {
  return date.getMonth() + 1 >= 4 ? date.getFullYear() : date.getFullYear() - 1;
}
function fySlug(start) {
  return `${start}-${String(start + 1).slice(-2)}`;
}
function fyPath(start) {
  return `/pnl/fy/${fySlug(start)}/`;
}
function monthPath(year, month) {
  return `/pnl/${year}/${month}/`;
}
function readPnlRoute(route) {
  const path = (route || "/pnl").split("?")[0].replace(/\/+$/, "") || "/pnl";
  const fy = path.match(/^\/pnl\/fy\/(\d{4})-(\d{2})$/);
  if (fy) {
    const start = Number(fy[1]);
    const end = String((start + 1) % 100).padStart(2, "0");
    if (end === fy[2]) return { kind: "year", fyStart: start };
  }
  const month = path.match(/^\/pnl\/(\d{4})\/(\d{1,2})$/);
  if (month) {
    const year = Number(month[1]);
    const m = Number(month[2]);
    if (m >= 1 && m <= 12) return { kind: "month", year, month: m, fyStart: m >= 4 ? year : year - 1 };
  }
  return { kind: "year", fyStart: currentFyStart() };
}
function fyOf(year, month) {
  return month >= 4 ? year : year - 1;
}

/* Company total for one month's summary, verticals folded together.
   Owner investment is money put in, not a result. */
function collapseSummary(summary) {
  const totals = {};
  let owner = 0;
  for (const vertical of Object.values(summary || {})) {
    for (const [category, amount] of Object.entries(vertical)) {
      const n = Number(amount) || 0;
      if (category === "Owner Investment") owner += n;
      else totals[category] = (totals[category] || 0) + n;
    }
  }
  const net = Object.values(totals).reduce((s, n) => s + n, 0);
  return { totals, owner, net };
}

function csvCell(value) {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}
function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

/* Same markers as spend_family() in statement_parsers.py. Keyword rules only.
   AI is a label on the spend, not a model call inside this screen. */
const AI_MARKERS = ["OPENAI", "ANTHROPIC", "CLAUDE", "GROK", "XAI", "SARVAM", "ELEVENLABS", "HEYGEN", "HIGGSFIELD", "RUNWAY", "DEEPSEEK", "PINECONE", "TURBOSCRIBE", "LOVABLE", "MIDJOURNEY", "CURSOR"];
const CLOUD_MARKERS = ["AMAZON WEB SERVICES", "AMAZONAWSESC", "GOOGLECLOUD", "GOOGLE CLOUD", "GOOGLE*COLAB", "GOOGLEWORKSP", "GOOGLE WORKSPACE", "CLOUDFLARE", "GITHUB", "GODADDY", "RUNPOD", "VERCEL", "NETLIFY", "DIGITALOCEAN", "SECURE DATA CEN", "FIGMA", "BREVO", "LINKEDIN", "KNOWLARITY", "EXOTEL"];
const PERSONAL_MARKERS = ["MEDICAL", "HOSPITAL", "MEDICO", "PHARMACY", "WELLNESS", "ALCHEMIST", "OJAS", "DIAGNOSTIC", "CLINIC", "APOLLO", "FORTIS", "PATHOLOG"];

function spendFamily(description) {
  const d = (description || "").toUpperCase();
  if (["FINANCE CHARGES", "FCY MARKUP", "DCC FEE", "DCC TRANSACTION", "CMS CHARGES"].some((k) => d.includes(k))) return "Card interest & fees";
  if (["CREDIT CARD PAYMENT", "AUTOPAY THANK YOU", " REV ", " REV(", "REFUND"].some((k) => ` ${d} `.includes(k)) || d.includes("NETBANKING TRANSFER") || d.startsWith("NEFT") || d.includes("PAYMENT RECEIVED")) return "Card payment";
  if (PERSONAL_MARKERS.some((k) => d.includes(k))) return "Personal";
  if (AI_MARKERS.some((k) => d.includes(k))) return "AI";
  if (CLOUD_MARKERS.some((k) => d.includes(k))) return "Cloud & code";
  return "Other";
}

/* Short form in the cell so columns stay narrow and aligned; the exact rupee
   figure rides along in the title so hovering gives the real number. */
function fmtINR(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) < 0.5) return "—";
  const abs = Math.abs(v);
  const sign = v < 0 ? "−" : "";
  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(1)}k`;
  return `${sign}₹${abs.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function exactINR(n) {
  const v = Number(n) || 0;
  return `${v < 0 ? "−" : ""}₹${Math.abs(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/* Every money cell: short label, exact value on hover, never wraps. */
function Money({ value, bold }) {
  const v = Number(value) || 0;
  return (
    <td
      title={exactINR(v)}
      style={{
        textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
        fontWeight: bold ? 700 : undefined,
        color: v < 0 ? "var(--red-600, #c0392b)" : v > 0 ? "var(--green-600, #1e8449)" : undefined,
      }}
    >
      {fmtINR(v)}
    </td>
  );
}

const periodKey = (p) => (p ? `${p.year}-${p.month}` : "all");

const FAMILY_ORDER = ["AI", "Cloud & code", "Card interest & fees", "Other", "Card payment"];

function LineTransactions({ label, rows, categories }) {
  const matched = rows.filter((t) => {
    if (t.status === "Excluded") return false;
    if ((t.spend_family || spendFamily(t.description)) === "Personal") return false;
    const name = t.category
      ? (categories.find((c) => String(c.id) === String(t.category))?.name || "")
      : "Uncategorized";
    return name === label;
  });
  if (!matched.length) {
    return <div style={{ fontSize: 13, color: "var(--c-500)", padding: "8px 4px" }}>No individual rows under this line for this month.</div>;
  }
  if (label === "Infra & SaaS") {
    const groups = new Map();
    for (const t of matched) {
      const vendor = t.vendor || "Other";
      if (!groups.has(vendor)) groups.set(vendor, []);
      groups.get(vendor).push(t);
    }
    const ordered = [...groups.entries()].sort((a, b) => {
      const sum = (list) => list.reduce((s, t) => s + (Number(t.amount) || 0), 0);
      return sum(a[1]) - sum(b[1]);
    });
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "8px 4px 4px" }}>
        {ordered.map(([vendor, list]) => {
          const total = list.reduce((s, t) => s + (Number(t.amount) || 0), 0);
          return (
            <details key={vendor} open={ordered.length === 1}>
              <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                {vendor} · {list.length} · <span title={exactINR(total)}>{fmtINR(total)}</span>
              </summary>
              <TxnLines list={list} />
            </details>
          );
        })}
      </div>
    );
  }
  return (
    <div style={{ padding: "8px 4px 4px" }}>
      <TxnLines list={matched} />
    </div>
  );
}

function TxnLines({ list }) {
  return (
    <table className="Erp-Table" style={{ width: "100%", fontSize: 13, marginTop: 6 }}>
      <tbody>
        {list.map((t) => (
          <tr key={t.id}>
            <td className="num" style={{ whiteSpace: "nowrap" }}>{t.transaction_date}</td>
            <td>{t.description}</td>
            <td style={{ whiteSpace: "nowrap" }}>{t.vertical || "Unassigned"}</td>
            <Money value={t.amount} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function CardParticulars({ rows }) {
  const totals = {};
  for (const name of FAMILY_ORDER) totals[name] = 0;
  for (const t of rows) {
    if (t.status === "Excluded" || (t.spend_family || spendFamily(t.description)) === "Personal") continue;
    const family = t.spend_family || spendFamily(t.description);
    totals[family] = (totals[family] || 0) + (Number(t.amount) || 0);
  }
  const shown = FAMILY_ORDER.filter((name) => Math.abs(totals[name] || 0) >= 0.5);
  if (!shown.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "0 0 12px" }}>
      {shown.map((name) => (
        <span key={name} title={exactINR(totals[name])} style={{
          fontSize: 13, padding: "4px 10px", borderRadius: 999,
          background: "var(--c-50, rgba(127,127,127,.08))",
          fontVariantNumeric: "tabular-nums",
        }}>
          {name} {fmtINR(totals[name])}
        </span>
      ))}
    </div>
  );
}

const YEAR_LINES = [...CATEGORY_ROWS, "Uncategorized"];

function StatementDot({ on }) {
  return (
    <span
      style={{
        width: 8, height: 8, borderRadius: 99, display: "inline-block",
        background: on ? "var(--green-600, #1e8449)" : "var(--c-200, #d5d5d5)",
      }}
    />
  );
}

function YearBoard({ pack, loading, navigate, periods }) {
  const start = pack?.fy_start;
  const months = pack?.months || [];
  const collapsed = months.map((m) => collapseSummary(m.summary));
  const yearTotals = {};
  let yearOwner = 0;
  for (const col of collapsed) {
    yearOwner += col.owner;
    for (const [category, amount] of Object.entries(col.totals)) {
      yearTotals[category] = (yearTotals[category] || 0) + amount;
    }
  }
  const yearNet = Object.values(yearTotals).reduce((s, n) => s + n, 0);
  const fyStarts = [...new Set((periods || []).map((p) => fyOf(p.year, p.month)))].sort((a, b) => b - a);
  if (start && !fyStarts.includes(start)) fyStarts.unshift(start);

  const download = () => {
    const header = ["Line", ...months.map((m) => `${MONTH_SHORT[m.month - 1]} ${m.year}`), "Year"];
    const body = YEAR_LINES.map((label) => [
      label,
      ...collapsed.map((col) => (col.totals[label] || 0).toFixed(2)),
      (yearTotals[label] || 0).toFixed(2),
    ]);
    body.push(["Net result", ...collapsed.map((col) => col.net.toFixed(2)), yearNet.toFixed(2)]);
    body.push(["Owner investment (not in the net)", ...collapsed.map((col) => col.owner.toFixed(2)), yearOwner.toFixed(2)]);
    downloadCsv(`ATGWORLD-PL-FY${fySlug(start)}.csv`, [
      ["ATGWORLD NETWORKS PRIVATE LIMITED"],
      [`Statement of profit and loss, FY ${fySlug(start)}`],
      [`1 April ${start} to 31 March ${start + 1}`],
      ["Figures in INR. Costs are negative. Personal card spend is excluded. Owner investment is not in the net."],
      [],
      header,
      ...body,
    ]);
  };

  return (
    <section style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Finance</span>
          <h1 style={{ margin: "4px 0 0", fontSize: 26, fontWeight: 700 }}>P&amp;L · FY {start ? fySlug(start) : ""}</h1>
          <div style={{ marginTop: 6, color: "var(--c-500)", fontSize: 13.5, maxWidth: 640 }}>
            1 April {start} to 31 March {start ? start + 1 : ""}. The company as one number.
            Click a month for the verticals and the transactions behind it.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <select
            value={start || ""}
            onChange={(e) => navigate(fyPath(Number(e.target.value)))}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-200)", fontWeight: 600 }}
          >
            {fyStarts.map((y) => <option key={y} value={y}>FY {fySlug(y)}</option>)}
          </select>
          <button className="Soft-Button Small" onClick={download} disabled={!months.length} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Download size={14} /> Download this statement
          </button>
        </div>
      </div>
      {loading ? <EmptyState label="Loading…" /> : (
        <Panel title={start ? `FY ${fySlug(start)}` : "Financial year"} subtitle="Amounts in INR. Two dots under each month: Kotak, then HDFC. Green means that statement is in. A * means the spreadsheet and the bank are both counted.">
          <div style={{ overflowX: "auto" }}>
            <table className="Erp-Table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Line</th>
                  {months.map((m) => (
                    <th key={`${m.year}-${m.month}`} style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button
                        type="button"
                        onClick={() => navigate(monthPath(m.year, m.month))}
                        title={m.conflict ? "This month is covered by both the spreadsheet and bank statements" : `Open ${MONTH_NAMES[m.month - 1]} ${m.year}`}
                        style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit", fontWeight: 700, color: "inherit" }}
                      >
                        {MONTH_SHORT[m.month - 1]}{m.conflict ? " *" : ""}
                      </button>
                    </th>
                  ))}
                  <th style={{ textAlign: "right" }}>Year</th>
                </tr>
                <tr>
                  <th style={{ textAlign: "left", fontWeight: 500, color: "var(--c-500)", fontSize: 12 }}>Kotak · HDFC</th>
                  {months.map((m) => (
                    <th key={`dots-${m.year}-${m.month}`} style={{ textAlign: "right" }}>
                      <span
                        title={`Kotak ${m.kotak ? "in" : "not in"}. HDFC ${m.hdfc ? "in" : "not in"}.`}
                        style={{ display: "inline-flex", gap: 4, justifyContent: "flex-end" }}
                      >
                        <StatementDot on={!!m.kotak} />
                        <StatementDot on={!!m.hdfc} />
                      </span>
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {YEAR_LINES.map((label) => (
                  <tr key={label} style={label === "Uncategorized" ? { fontStyle: "italic", color: "var(--c-500)" } : undefined}>
                    <td style={{ whiteSpace: "nowrap" }}>{label === "Uncategorized" ? "Not yet categorised" : label}</td>
                    {collapsed.map((col, i) => <Money key={i} value={col.totals[label] || 0} />)}
                    <Money value={yearTotals[label] || 0} bold />
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, background: "var(--c-50, rgba(127,127,127,.08))" }}>
                  <td>Net result</td>
                  {collapsed.map((col, i) => <Money key={i} value={col.net} bold />)}
                  <Money value={yearNet} bold />
                </tr>
                <tr style={{ fontStyle: "italic", color: "var(--c-500)", fontSize: 13 }}>
                  <td>Owner investment (not in the net)</td>
                  {collapsed.map((col, i) => <Money key={i} value={col.owner} />)}
                  <Money value={yearOwner} />
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </section>
  );
}

export function PnLScreen({ navigate, route }) {
  const view = readPnlRoute(route);
  const selectedPeriod = view.kind === "month" ? `${view.year}-${view.month}` : "year";
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({});
  const [yearPack, setYearPack] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  // A failed load or a refused change is said out loud: empty figures would read as empty books, and a row
  // that kept its new value would read as saved.
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");

  const [dragging, setDragging] = useState(false);
  const [queue, setQueue] = useState([]); // [{name, status: pending|working|done|failed, message}]
  const [sources, setSources] = useState([]);
  const [conflict, setConflict] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [openTables, setOpenTables] = useState({});
  const [lastUpload, setLastUpload] = useState(null);
  const [openLine, setOpenLine] = useState(null);

  const loadReference = useCallback(async () => {
    const catRes = await apiGet("/FinanceAndPayroll/TransactionCategories/");
    setCategories(unpackList(catRes));
  }, []);

  const uploading = queue.some((row) => row.status === "working");

  const loadLedger = useCallback(async (v) => {
    setLoading(true);
    setLoadError("");
    try {
      if (v.kind === "year") {
        const sumRes = await apiGet(`/FinanceAndPayroll/Transactions/pnl-summary/?fy=${v.fyStart}`);
        setYearPack(sumRes);
        setPeriods(sumRes?.periods || []);
        setTransactions([]);
        setSummary({});
        setSources([]);
        setConflict(null);
        return;
      }
      const query = `?year=${v.year}&month=${v.month}`;
      const [sumRes, txRes] = await Promise.all([
        apiGet(`/FinanceAndPayroll/Transactions/pnl-summary/${query}`),
        apiGet("/FinanceAndPayroll/Transactions/"),
      ]);
      setSummary(sumRes?.summary || {});
      setPeriods(sumRes?.periods || []);
      setSources(sumRes?.sources || []);
      setConflict(sumRes?.conflict || null);
      setTransactions(unpackList(txRes));
    } catch (err) {
      setSummary({}); setSources([]); setConflict(null); setTransactions([]);
      setLoadError("The figures could not be loaded, so nothing below is the real picture.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReference(); }, [loadReference]);
  useEffect(() => { loadLedger(view); }, [loadLedger, view.kind, view.fyStart, view.year, view.month]);

  /* /pnl/ is the front door. Put the current financial year in the address
     bar immediately, so the link that gets copied is a specific year. */
  useEffect(() => {
    const path = (route || "").split("?")[0].replace(/\/+$/, "");
    if (navigate && (path === "/pnl" || path === "")) navigate(fyPath(currentFyStart()), { replace: true });
  }, [route, navigate]);

  /* ── uploads: many files at once, one request each, run in the background.
     No account picker and no password box — the server works out which
     account a file belongs to from the file itself, and knows the card
     password. ── */
  const runUploads = useCallback(async (files) => {
    if (!files.length) return;
    const started = files.map((f) => ({ name: f.name, status: "pending", message: "" }));
    setQueue(started);

    for (let i = 0; i < files.length; i++) {
      setQueue((prev) => prev.map((row, idx) => (idx === i ? { ...row, status: "working" } : row)));
      try {
        const form = new FormData();
        form.append("file", files[i]);
        const res = await apiPost("/FinanceAndPayroll/StatementUploads/upload-and-parse/", form);
        const months = (res?.months || [])
          .map((m) => {
            const [y, mm] = m.period.split("-");
            return `${MONTH_NAMES[Number(mm) - 1]} ${y} (${m.transactions})`;
          })
          .join(", ");
        setQueue((prev) => prev.map((row, idx) => (idx === i
          ? {
            ...row, status: "done",
            message: `${res?.account || "account"} · ${res?.created ?? 0} added${res?.duplicates_skipped ? `, ${res.duplicates_skipped} already there` : ""}`,
          }
          : row)));
        if (res?.created) {
          setLastUpload({
            account: res.account,
            months,
            created: res.created,
            uncategorized: res.uncategorized || 0,
            unparsed: res.unparsed || 0,
          });
        }
      } catch (err) {
        setQueue((prev) => prev.map((row, idx) => (idx === i
          ? { ...row, status: "failed", message: err?.payload?.detail || err?.message || "Failed" }
          : row)));
      }
    }
    await loadLedger(view);
  }, [loadLedger, view]);

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    runUploads(Array.from(event.dataTransfer.files || []));
  };

  const updateTransaction = async (id, patch) => {
    setActionError("");
    const saved = await editRowOptimistically({
      rows: transactions, id, patch, setRows: setTransactions,
      save: () => apiPatch(`/FinanceAndPayroll/Transactions/${id}/`, patch),
      onFail: (err) => setActionError(err?.payload?.detail || err?.message || "That change was not saved."),
    });
    if (saved) await loadLedger(view);
  };

  /* Split this month's salary across the verticals using who was actually on
     which project. Month-scoped on purpose — assignments change, so a blanket
     "do all time" would apply today's team to last year's payroll. */
  const [splitting, setSplitting] = useState(null);
  const autoAssignSalaries = async () => {
    if (selectedPeriod === "all") return;
    const [year, month] = selectedPeriod.split("-");
    setSplitting("working");
    try {
      const res = await apiPost("/FinanceAndPayroll/Transactions/auto-assign-salaries/", { year, month });
      setSplitting(res?.assigned
        ? `Split ${res.parents_split} salary payment(s) across ${Object.keys(res.shares || {}).length} verticals.`
        : (res?.detail || "Nothing left to split."));
      await loadLedger(view);
    } catch (err) {
      setSplitting(err?.payload?.detail || err?.message || "Couldn't split salaries.");
    }
  };

  const resolveConflict = async (keep) => {
    if (selectedPeriod === "all") return;
    const [year, month] = selectedPeriod.split("-");
    setResolving(true);
    setActionError("");
    try {
      await apiPost("/FinanceAndPayroll/Transactions/resolve-conflict/", { year, month, keep });
      await loadLedger(view);
    } catch (err) {
      setActionError(err?.payload?.detail || err?.message || "That could not be settled.");
    } finally {
      setResolving(false);
    }
  };

  const visibleTransactions = useMemo(() => {
    if (view.kind !== "month") return [];
    return transactions.filter((t) => {
      const d = new Date(t.transaction_date);
      return d.getFullYear() === view.year && d.getMonth() + 1 === view.month;
    });
  }, [transactions, view]);

  /* A salary payment that has been divided across verticals is stored as the
     original row (kept, but not counted, so nothing is double counted) plus one
     child row per vertical. Both need to read as exactly that — an earlier
     version just labelled the original "Excluded", which looks identical to a
     personal charge deliberately dropped from the P&L and had him reasonably
     asking why his salary payments were being left out. */
  const splitParentIds = useMemo(() => {
    const ids = new Set();
    for (const t of visibleTransactions) {
      const parent = t.metadata?.split_from;
      if (parent) ids.add(Number(parent));
    }
    return ids;
  }, [visibleTransactions]);

  const transactionGroups = useMemo(() => {
    const groups = new Map();
    for (const t of visibleTransactions) {
      const account = t.account_name || "Unknown account";
      if (!groups.has(account)) {
        groups.set(account, {
          account, rows: [], net: 0,
          isPersonalCard: t.account_type === "Credit Card",
          isHistorical: account === "Historical P&L sheets",
        });
      }
      groups.get(account).rows.push(t);
      const personal = (t.spend_family || spendFamily(t.description)) === "Personal";
      if (t.status !== "Excluded" && !personal) groups.get(account).net += Number(t.amount) || 0;
    }
    // Children immediately under the payment they came out of, so the split is
    // readable as one thing rather than scattered rows that look unrelated.
    for (const group of groups.values()) {
      const children = new Map();
      for (const t of group.rows) {
        const parent = t.metadata?.split_from;
        if (parent) {
          if (!children.has(Number(parent))) children.set(Number(parent), []);
          children.get(Number(parent)).push(t);
        }
      }
      const ordered = [];
      for (const t of group.rows) {
        if (t.metadata?.split_from) continue;
        ordered.push(t);
        for (const child of children.get(t.id) || []) ordered.push(child);
      }
      // A child whose parent isn't in this period still has to appear.
      const placed = new Set(ordered.map((r) => r.id));
      for (const t of group.rows) if (!placed.has(t.id)) ordered.push(t);
      group.rows = ordered;
    }
    // Kotak and the card first — that is what he checks — with the historical
    // spreadsheet summary last, since it is reference rather than working data.
    return [...groups.values()].sort((a, b) =>
      Number(a.isHistorical) - Number(b.isHistorical)
      || Number(a.isPersonalCard) - Number(b.isPersonalCard));
  }, [visibleTransactions]);

  const pivot = useMemo(() => {
    const extras = Object.keys(summary || {})
      .filter((v) => v && v !== "Unassigned" && !ALWAYS_SHOWN.includes(v))
      .sort();
    const columns = [...ALWAYS_SHOWN, ...extras, "Unassigned"];
    const rows = CATEGORY_ROWS.map((label) => {
      const values = columns.map((v) => (summary?.[v]?.[label]) || 0);
      return { label, values, total: values.reduce((s, x) => s + x, 0) };
    });
    const netByCol = columns.map((_, i) => rows.reduce((s, r) => s + r.values[i], 0));
    const ownerRow = columns.map((v) => (summary?.[v]?.["Owner Investment"]) || 0);
    const uncatRow = columns.map((v) => (summary?.[v]?.["Uncategorized"]) || 0);
    return { columns, rows, netByCol, netTotal: netByCol.reduce((s, x) => s + x, 0), ownerRow, uncatRow };
  }, [summary]);

  const periodLabel = `${MONTH_NAMES[view.month - 1]} ${view.year}`;

  const downloadMonth = () => {
    const rows = [
      ["ATGWORLD NETWORKS PRIVATE LIMITED"],
      [`Statement of profit and loss, ${periodLabel}`],
      ["Figures in INR. Costs are negative. Personal card spend is excluded from the net."],
      [],
      ["Line", ...pivot.columns, "Total"],
      ...pivot.rows.map((r) => [r.label, ...r.values.map((v) => Number(v).toFixed(2)), r.total.toFixed(2)]),
      ["Net result", ...pivot.netByCol.map((v) => Number(v).toFixed(2)), pivot.netTotal.toFixed(2)],
      [],
      ["Date", "Account", "Description", "Amount", "Category", "Vertical", "In the P&L"],
      ...visibleTransactions.map((t) => [
        t.transaction_date,
        t.account_name || "",
        t.description,
        Number(t.amount).toFixed(2),
        categories.find((c) => String(c.id) === String(t.category))?.name || (t.category ? "" : "Uncategorized"),
        t.vertical || "",
        (t.status === "Excluded" || (t.spend_family || spendFamily(t.description)) === "Personal") ? "No" : "Yes",
      ]),
    ];
    downloadCsv(`ATGWORLD-PL-${view.year}-${String(view.month).padStart(2, "0")}.csv`, rows);
  };

  if (view.kind === "year") {
    return <YearBoard pack={yearPack} loading={loading} navigate={navigate} periods={periods} />;
  }

  const statusIcon = { working: <Loader2 size={13} className="spin" />, done: <CheckCircle2 size={13} />, failed: <XCircle size={13} /> };

  return (
    /* One flex column with a single gap, so the distance between every box is
       identical by construction rather than by whatever margins each child
       happens to carry — which is what made the spacing read as uneven. */
    <section style={{
      padding: "24px 28px",
      display: "flex", flexDirection: "column", gap: 18,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Finance</span>
          <h1 style={{ margin: "4px 0 0", fontSize: 26, fontWeight: 700 }}>{periodLabel}</h1>
          <button
            type="button"
            onClick={() => navigate(fyPath(view.fyStart))}
            style={{ marginTop: 4, background: "none", border: 0, padding: 0, cursor: "pointer", color: "var(--c-500)", fontSize: 13 }}
          >
            ← FY {fySlug(view.fyStart)}
          </button>
        </div>
        <div
          style={{ display: "flex", gap: 10, alignItems: "center" }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <select
            value={selectedPeriod}
            onChange={(e) => {
              const [y, m] = e.target.value.split("-");
              navigate(monthPath(Number(y), Number(m)));
            }}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-200)", fontWeight: 600 }}
          >
            {!periods.some((p) => periodKey(p) === selectedPeriod) && (
              <option value={selectedPeriod}>{periodLabel} (nothing yet)</option>
            )}
            {periods.map((p) => (
              <option key={periodKey(p)} value={periodKey(p)}>{MONTH_NAMES[p.month - 1]} {p.year}</option>
            ))}
          </select>
          <button className="Soft-Button Small" onClick={downloadMonth} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Download size={14} /> Download this statement
          </button>

          {/* Upload lives here rather than in a panel of its own: it is a
              once-a-month action, not the point of the page. Drop files
              anywhere on this control, or click it. */}
          <label
            className="Soft-Button Small"
            style={{
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
              borderStyle: dragging ? "dashed" : undefined,
              borderColor: dragging ? "var(--blue-600, #2563eb)" : undefined,
            }}
            title="Kotak CSV or HDFC PDF — one month at a time is easiest, so you can check what came out before adding the next"
          >
            <input
              type="file" accept=".csv,.pdf" multiple style={{ display: "none" }}
              onChange={(e) => { runUploads(Array.from(e.target.files || [])); e.target.value = ""; }}
            />
            <UploadCloud size={14} />
            {uploading ? "Reading…" : dragging ? "Drop to upload" : "Upload statements"}
          </label>

          {selectedPeriod !== "all" && (
            <button className="Soft-Button Small" onClick={autoAssignSalaries} disabled={splitting === "working"}>
              <Split size={14} /> {splitting === "working" ? "Splitting…" : "Split salaries by vertical"}
            </button>
          )}
        </div>
      </div>

      {(loadError || actionError) && (
        <div className="Auth-Alert">
          {loadError || actionError}
          {loadError && <button className="Soft-Button Small" style={{ marginLeft: 10 }} onClick={() => loadLedger(selectedPeriod)}>Try again</button>}
        </div>
      )}

      {splitting && splitting !== "working" && (
        <div style={{ fontSize: 13, color: "var(--c-500)" }}>{splitting}</div>
      )}

      {/* What the last upload actually did, named by month — the months come
          from the transaction dates in the file, so a card cycle spanning two
          months says so rather than being filed under one. */}
      {lastUpload && (
        <div
          style={{
            padding: "12px 14px", borderRadius: 10, fontSize: 13.5,
            border: "1px solid var(--green-600, #1e8449)", background: "var(--green-50, rgba(30,132,73,.08))",
          }}
        >
          <b>Processed — {lastUpload.months || "no dated rows"} updated.</b>{" "}
          {lastUpload.created} transactions read from the {lastUpload.account} statement
          {lastUpload.uncategorized > 0 && `, ${lastUpload.uncategorized} of them couldn't be categorised automatically and are waiting for you below`}
          {lastUpload.unparsed > 0 && `, ${lastUpload.unparsed} line(s) couldn't be read at all`}.
          <button className="Soft-Button Small" style={{ marginLeft: 10 }} onClick={() => setLastUpload(null)}>Dismiss</button>
        </div>
      )}

      {/* Both a spreadsheet figure and real bank statements for the same month
          means that month is counted twice. Surfaced to settle, never merged
          silently. */}
      {conflict && (
        <div
          style={{
            padding: "14px 16px", borderRadius: 10, fontSize: 13.5,
            border: "1px solid var(--gold-600, #b8862e)", background: "var(--gold-50, rgba(184,134,46,.10))",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            <AlertTriangle size={15} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            {periodLabel} is covered twice — these figures are being double counted
          </div>
          <div style={{ color: "var(--c-500)", marginBottom: 10 }}>
            The old spreadsheet has a summary for this month
            {" "}(<b title={exactINR(conflict.historical.net)}>{fmtINR(conflict.historical.net)}</b> across {conflict.historical.transactions} rows),
            and so do real statements from {conflict.bank.accounts.join(" and ")}
            {" "}(<b title={exactINR(conflict.bank.net)}>{fmtINR(conflict.bank.net)}</b> across {conflict.bank.transactions} rows).
            Keep one. Nothing is deleted — the other side is just left out of the P&L and can be switched back.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="Primary-Button Small" disabled={resolving} onClick={() => resolveConflict("bank")}>
              Keep the bank statements
            </button>
            <button className="Soft-Button Small" disabled={resolving} onClick={() => resolveConflict("historical")}>
              Keep the old spreadsheet figure
            </button>
          </div>
        </div>
      )}

      {queue.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {queue.map((row, i) => (
            <div key={`${row.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span style={{ color: row.status === "failed" ? "var(--red-600, #c0392b)" : row.status === "done" ? "var(--green-600, #1e8449)" : "var(--c-500)" }}>
                {statusIcon[row.status] || "•"}
              </span>
              <span style={{ fontWeight: 500 }}>{row.name}</span>
              <span style={{ color: "var(--c-500)" }}>{row.message}</span>
            </div>
          ))}
        </div>
      )}

      <Panel
        title={`P&L — ${periodLabel}`}
        subtitle={loadError ? "" : sources.length
          ? `On file for this period: ${sources.map((s) => `${s.account} (${s.transactions})`).join(" · ")}`
          : "Nothing on file for this period yet."}
      >
        {loading ? <EmptyState label="Loading…" /> : loadError ? null : (
          <div style={{ overflowX: "auto" }}>
            <table className="Erp-Table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Line</th>
                  {pivot.columns.map((c) => (
                    <th key={c} style={{ textAlign: "right", whiteSpace: "nowrap" }}>{c}</th>
                  ))}
                  <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {pivot.rows.map((r) => {
                  const open = openLine === r.label;
                  return (
                    <Fragment key={r.label}>
                      <tr>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            onClick={() => setOpenLine(open ? null : r.label)}
                            style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit", color: "inherit" }}
                          >
                            {open ? "▾" : "▸"} {r.label}
                          </button>
                        </td>
                        {r.values.map((v, i) => <Money key={i} value={v} />)}
                        <Money value={r.total} bold />
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={pivot.columns.length + 2} style={{ background: "var(--c-50, rgba(127,127,127,.06))" }}>
                            <LineTransactions label={r.label} rows={visibleTransactions} categories={categories} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                <tr style={{ fontWeight: 700, background: "var(--c-50, rgba(127,127,127,.08))" }}>
                  <td style={{ whiteSpace: "nowrap" }}>Net result</td>
                  {pivot.netByCol.map((v, i) => <Money key={i} value={v} bold />)}
                  <Money value={pivot.netTotal} bold />
                </tr>
                <tr style={{ fontStyle: "italic", color: "var(--c-500)", fontSize: 13 }}>
                  <td>Owner investment (financing, not revenue)</td>
                  {pivot.ownerRow.map((v, i) => <Money key={i} value={v} />)}
                  <Money value={pivot.ownerRow.reduce((s, x) => s + x, 0)} />
                </tr>
                <tr style={{ fontStyle: "italic", color: "var(--c-500)", fontSize: 13 }}>
                  <td>
                    <button
                      type="button"
                      onClick={() => setOpenLine(openLine === "Uncategorized" ? null : "Uncategorized")}
                      style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit", color: "inherit", fontStyle: "italic" }}
                    >
                      {openLine === "Uncategorized" ? "▾" : "▸"} Uncategorized — needs review
                    </button>
                  </td>
                  {pivot.uncatRow.map((v, i) => <Money key={i} value={v} />)}
                  <Money value={pivot.uncatRow.reduce((s, x) => s + x, 0)} />
                </tr>
                {openLine === "Uncategorized" && (
                  <tr>
                    <td colSpan={pivot.columns.length + 2}>
                      <LineTransactions label="Uncategorized" rows={visibleTransactions} categories={categories} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Split by account rather than one long list. The Kotak account is the
          company's; the HDFC card is a personal card that happens to carry
          company spend, so some of its rows are personal and belong nowhere
          near this P&L. Keeping them apart is the whole point. */}
      {transactionGroups.map(({ account, rows, isPersonalCard, isHistorical, net }) => (
        <Panel
          key={account}
          title={
            <span
              style={{ cursor: "pointer", userSelect: "none" }}
              onClick={() => setOpenTables((prev) => ({ ...prev, [account]: !prev[account] }))}
            >
              {openTables[account] ? "▾" : "▸"} {account} — {periodLabel}
              <span style={{ fontWeight: 400, color: "var(--c-500)", marginLeft: 10, fontSize: 13 }}>
                {rows.length} rows · <span title={exactINR(net)}>{fmtINR(net)}</span>
              </span>
            </span>
          }
          className={openTables[account] ? "" : "Panel-Collapsed"}
          subtitle={isHistorical
            ? "A summary from the old spreadsheets, not bank data — one figure per vertical per month."
            : isPersonalCard
              ? "Personal card. Company spend on it (AI, cloud, code) stays in the P&L. Medical and the same kind of personal spend is left out."
              : "Company current account. Click a category or vertical to correct it — saved immediately."}
        >
          {isPersonalCard && (
            <CardParticulars rows={rows} />
          )}
          <div style={{ overflowX: "auto" }}>
            <table className="Erp-Table" style={{ width: "100%", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Date</th>
                  <th style={{ textAlign: "left" }}>Description</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th style={{ textAlign: "left" }}>Category</th>
                  <th style={{ textAlign: "left" }}>Vertical</th>
                  <th style={{ textAlign: "left" }}>In P&amp;L?</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const isSplitParent = splitParentIds.has(t.id);
                  const isSplitChild = Boolean(t.metadata?.split_from);
                  const personal = (t.spend_family || spendFamily(t.description)) === "Personal";
                  const excluded = t.status === "Excluded" || personal;
                  return (
                    <tr key={t.id} style={isSplitParent ? { opacity: 0.6 } : excluded ? { opacity: 0.45 } : undefined}>
                      <td className="num" style={{ whiteSpace: "nowrap" }}>{t.transaction_date}</td>
                      <td style={isSplitChild ? { paddingLeft: 22, color: "var(--c-500)" } : undefined}>
                        {isSplitChild && "↳ "}{t.description}
                      </td>
                      <Money value={t.amount} />
                      <td>
                        <select
                          value={t.category || ""} disabled={isSplitParent}
                          onChange={(e) => updateTransaction(t.id, { category: e.target.value || null })}
                        >
                          <option value="">Uncategorized</option>
                          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </td>
                      <td>
                        <select
                          value={t.vertical || ""} disabled={isSplitParent}
                          onChange={(e) => updateTransaction(t.id, { vertical: e.target.value })}
                        >
                          <option value="">Unassigned</option>
                          {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </td>
                      <td>
                        {isSplitParent ? (
                          <span
                            className="Status-Pill Blue"
                            title="Divided across verticals — the ↳ rows below carry the amounts, so this one isn't counted again. Nothing is lost."
                          >
                            Split
                          </span>
                        ) : personal ? (
                          <span className="Status-Pill" title="Personal spend on this card. Left out of the company P&L.">Personal</span>
                        ) : (
                          <button
                            className="Soft-Button Small"
                            title={excluded ? "Left out of the P&L" : "Counted in the P&L — click to leave it out"}
                            onClick={() => updateTransaction(t.id, { status: excluded ? "Categorized" : "Excluded" })}
                          >
                            {excluded ? "Excluded" : "Counted"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}
      {!visibleTransactions.length && !loadError && (
        <Panel title={`Transactions — ${periodLabel}`}><EmptyState label="Nothing in this period yet." /></Panel>
      )}
    </section>
  );
}

export default PnLScreen;
